"use client";

import { Plane, Clock, Luggage, Info } from "lucide-react";
import { intervalToDuration, type Duration } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { iFlightLegDetails } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import {
  formatConsistentDate,
  parseLocalDateTime,
} from "@/utils/timezone-date-utils";
import { useAirportList } from "../helpers/load-airport-list";
import { useAirportTimezones } from "../helpers/load-airport-timezones";
import { useAirlineList } from "../helpers/load-airline-list";
import { Divider } from "@/components/ui/divider";

export interface FlightItineraryInnerProps extends iFlightLegDetails {
  primaryColor?: string;
  layout?: "layout_1" | "layout_2";
}

export function FlightItineraryInner(props: FlightItineraryInnerProps) {
  const airportList = useAirportList();
  const airportTimezones = useAirportTimezones();
  const airlineList = useAirlineList();
  const primaryColor = quoteThemeCssVars.primary;
  const lightColor = quoteThemeCssVars.primary10;
  const layout = props.layout ?? "layout_2";

  const formatDate = (
    dateString: string | undefined,
    formatString: string
  ): string => {
    if (!dateString) return "Invalid date";
    return formatConsistentDate(dateString, formatString) || "Invalid date";
  };

  const departureAirportDetails = airportList.find(
    (a) => a.iata_code === props.departureAirport
  );
  const arrivalAirportDetails = airportList.find(
    (a) => a.iata_code === props.arrivalAirport
  );
  const departureTimezone = airportTimezones.find(
    (a) => a.code === props.departureAirport
  )?.timezone;
  const arrivalTimezone = airportTimezones.find(
    (a) => a.code === props.arrivalAirport
  )?.timezone;
  const formattedDepartureAirport = departureAirportDetails
    ? `${departureAirportDetails.name} (${props.departureAirport})`
    : props.departureAirport;
  const formattedArrivalAirport = arrivalAirportDetails
    ? `${arrivalAirportDetails.name} (${props.arrivalAirport})`
    : props.arrivalAirport;
  const airlineDetails = airlineList.find(
    (a) => a.name === props.airline || a.id === props.airline
  );

  const calculateDuration = (): Duration | null => {
    try {
      if (
        !props.departureDate ||
        !props.arrivalDate ||
        !props.departureTime ||
        !props.arrivalTime
      )
        return null;
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(props.departureDate) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(props.arrivalDate) ||
        !/^\d{2}:\d{2}/.test(props.departureTime) ||
        !/^\d{2}:\d{2}/.test(props.arrivalTime)
      )
        return null;
      const depDate = parseLocalDateTime(
        props.departureDate,
        props.departureTime.slice(0, 5)
      );
      const arrDate = parseLocalDateTime(
        props.arrivalDate,
        props.arrivalTime.slice(0, 5)
      );
      if (!depDate || !arrDate) return null;
      const depDateUTC = fromZonedTime(depDate, departureTimezone || "UTC");
      const arrDateUTC = fromZonedTime(arrDate, arrivalTimezone || "UTC");
      if (isNaN(depDateUTC.getTime()) || isNaN(arrDateUTC.getTime())) return null;
      return intervalToDuration({ start: depDateUTC, end: arrDateUTC });
    } catch {
      return null;
    }
  };

  const duration = calculateDuration();
  const formatFlightDuration = (d: Duration) => {
    const hours = d.hours || 0;
    const minutes = d.minutes || 0;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h${minutes ? ` ${minutes}m` : ""}`;
  };
  const formatTransitTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  if (layout === "layout_1") {
    return (
      <div className="p-4 sm:p-6 relative">
        <div className="md:hidden mb-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-xs text-gray-600 mb-1">Dep</div>
              <div className="text-base font-bold truncate">{props.departureAirport}</div>
              <div className="text-xs text-gray-800">
                {props.departureDate
                  ? formatDate(props.departureDate, "EEE, do MMM")
                  : "Date not available"}
              </div>
              {props.departureTime && (
                <div className="text-xs text-gray-600">{props.departureTime}</div>
              )}
            </div>
            <div className="flex flex-col items-center mx-2">
              <div className="flex flex-col items-center mb-0.5">
                <div className="w-4 h-4 border border-solid p-1 rounded-full border-gray-200 flex items-center justify-center flex-shrink-0 mb-0.5">
                  <img
                    src={`/assets/airline-logos/${airlineDetails?.id?.toLowerCase()}.png`}
                    alt={airlineDetails?.name || props.airline}
                    className="w-full h-full"
                  />
                </div>
                <div className="hidden md:flex items-center gap-1">
                  <div className="text-xs text-gray-700">
                    {airlineDetails?.name || props.airline}
                    {props.flightNumber && (
                      <span className="text-gray-500 ml-1">• {props.flightNumber}</span>
                    )}
                  </div>
                </div>
                {props.cabinClass && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] mt-0.5"
                    style={{ backgroundColor: lightColor, color: primaryColor }}
                  >
                    {props.cabinClass}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <div className="w-8 h-0.5 bg-gray-300" />
                <Plane className="w-3 h-3 transform rotate-45" style={{ color: primaryColor }} />
                <div className="w-8 h-0.5 bg-gray-300" />
              </div>
              {duration && (
                <div className="text-xs text-gray-600 mt-1">
                  {formatFlightDuration(duration)}
                </div>
              )}
            </div>
            <div className="flex-1 text-right">
              <div className="text-xs text-gray-600 mb-1">Arr</div>
              <div className="text-base font-bold truncate">{props.arrivalAirport}</div>
              <div className="text-xs text-gray-800">
                {props.arrivalDate
                  ? formatDate(props.arrivalDate, "EEE, do MMM")
                  : "Date not available"}
              </div>
              {props.arrivalTime && (
                <div className="text-xs text-gray-600">{props.arrivalTime}</div>
              )}
            </div>
          </div>
        </div>
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-600 mb-1">Departure</div>
            <div className="text-xl font-bold">{formattedDepartureAirport}</div>
            <div className="text-base text-gray-800">
              {props.departureDate
                ? formatDate(props.departureDate, "EEE, do MMM yyyy")
                : "Date not available"}
            </div>
            {props.departureTime ? (
              <div className="text-sm text-gray-600">
                {props.departureTime}
                {departureTimezone && ` (${departureTimezone})`}
              </div>
            ) : (
              <div className="text-sm text-gray-600">Time not available</div>
            )}
          </div>
          <div className="flex flex-col items-center justify-center">
            <div className="flex flex-col items-center mb-1">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 sm:w-5 sm:h-5 border border-solid p-1 rounded-full border-gray-200 flex items-center justify-center flex-shrink-0">
                  <img
                    src={`/assets/airline-logos/${airlineDetails?.id?.toLowerCase()}.png`}
                    alt={airlineDetails?.name || props.airline}
                    className="w-full h-full"
                  />
                </div>
                <div className="text-base font-medium">
                  {airlineDetails?.name || props.airline}
                  {props.flightNumber && (
                    <span className="text-sm text-gray-500 ml-1.5">• {props.flightNumber}</span>
                  )}
                </div>
              </div>
              {props.cabinClass && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs sm:text-sm mt-1"
                  style={{ backgroundColor: lightColor, color: primaryColor }}
                >
                  {props.cabinClass}
                </span>
              )}
            </div>
            <Divider className="w-full">
              <Plane className="w-5 h-5 transform rotate-45" style={{ color: primaryColor }} />
            </Divider>
            {duration && (
              <div className="text-sm text-gray-600 mt-2">
                {formatFlightDuration(duration)}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600 mb-1">Arrival</div>
            <div className="text-xl font-bold">{formattedArrivalAirport}</div>
            <div className="text-base text-gray-800">
              {props.arrivalDate
                ? formatDate(props.arrivalDate, "EEE, do MMM yyyy")
                : "Date not available"}
            </div>
            {props.arrivalTime ? (
              <div className="text-sm text-gray-600">
                {props.arrivalTime}
                {arrivalTimezone && ` (${arrivalTimezone})`}
              </div>
            ) : (
              <div className="text-sm text-gray-600">Time not available</div>
            )}
          </div>
        </div>
        {props.transitTime && (
          <div className="absolute left-1/2 transform -translate-x-1/2 bottom-0 translate-y-full pt-1">
            <div className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
              <Clock className="w-3 h-3" style={{ color: primaryColor }} />
              <span>
                <span className="hidden sm:inline">Transit: </span>
                <span className="sm:hidden">Transit: </span>
                {formatTransitTime(props.transitTime)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h3 className="quote-itinerary-item-title text-base sm:text-lg font-semibold">
            {airlineDetails?.name || props.airline}
          </h3>
          <p className="text-sm sm:text-base text-gray-600">{props.flightNumber}</p>
        </div>
        <div className="text-right">
          {props.cabinClass && (
            <span
              className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
              style={{ backgroundColor: lightColor, color: primaryColor }}
            >
              {props.cabinClass}
            </span>
          )}
        </div>
      </div>
      <div className="md:hidden mb-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-xs text-gray-600 mb-1">Dep</div>
            <div className="text-base font-bold truncate">{props.departureAirport}</div>
            <div className="text-xs text-gray-800">
              {props.departureDate
                ? formatDate(props.departureDate, "EEE, do MMM")
                : "Date not available"}
            </div>
            {props.departureTime && (
              <div className="text-xs text-gray-600">{props.departureTime}</div>
            )}
          </div>
          <div className="flex flex-col items-center mx-2">
            <div className="w-8 h-8 border border-solid rounded-full border-gray-200 flex items-center justify-center mb-1">
              <img
                src={`/assets/airline-logos/${airlineDetails?.id?.toLowerCase()}.png`}
                alt={airlineDetails?.name || props.airline}
                className="w-full h-full"
              />
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-0.5 bg-gray-300" />
              <Plane className="w-3 h-3 transform rotate-45" style={{ color: primaryColor }} />
              <div className="w-8 h-0.5 bg-gray-300" />
            </div>
            {duration && (
              <div className="text-xs text-gray-600 mt-1">
                {formatFlightDuration(duration)}
              </div>
            )}
          </div>
          <div className="flex-1 text-right">
            <div className="text-xs text-gray-600 mb-1">Arr</div>
            <div className="text-base font-bold truncate">{props.arrivalAirport}</div>
            <div className="text-xs text-gray-800">
              {props.arrivalDate
                ? formatDate(props.arrivalDate, "EEE, do MMM")
                : "Date not available"}
            </div>
            {props.arrivalTime && (
              <div className="text-xs text-gray-600">{props.arrivalTime}</div>
            )}
          </div>
        </div>
      </div>
      <div className="hidden md:grid md:grid-cols-3 gap-6 mb-6">
        <div>
          <div className="text-sm text-gray-600 mb-1">Departure</div>
          <div className="text-xl font-bold">{formattedDepartureAirport}</div>
          <div className="text-base text-gray-800">
            {props.departureDate
              ? formatDate(props.departureDate, "EEE, do MMM yyyy")
              : "Date not available"}
          </div>
          {props.departureTime ? (
            <div className="text-sm text-gray-600">
              {props.departureTime}
              {departureTimezone && ` (${departureTimezone})`}
            </div>
          ) : (
            <div className="text-sm text-gray-600">Time not available</div>
          )}
        </div>
        <div className="flex flex-col items-center justify-center">
          <div className="p-2 -mt-12 mb-2 w-12 h-12 border border-solid rounded-full border-gray-200 flex items-center justify-center">
            <img
              src={`/assets/airline-logos/${airlineDetails?.id?.toLowerCase()}.png`}
              alt={airlineDetails?.name || props.airline}
              className="w-full h-full"
            />
          </div>
          <Divider className="w-full">
            <Plane className="w-5 h-5 transform rotate-45" style={{ color: primaryColor }} />
          </Divider>
          {duration && (
            <div className="text-sm text-gray-600 mt-2">
              {formatFlightDuration(duration)}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-600 mb-1">Arrival</div>
          <div className="text-xl font-bold">{formattedArrivalAirport}</div>
          <div className="text-base text-gray-800">
            {props.arrivalDate
              ? formatDate(props.arrivalDate, "EEE, do MMM yyyy")
              : "Date not available"}
          </div>
          {props.arrivalTime ? (
            <div className="text-sm text-gray-600">
              {props.arrivalTime}
              {arrivalTimezone && ` (${arrivalTimezone})`}
            </div>
          ) : (
            <div className="text-sm text-gray-600">Time not available</div>
          )}
        </div>
      </div>
      {(props.flightCheckInTime ||
        props.handLuggageOnly ||
        props.baggageAllowance ||
        props.baggageNotes ||
        props.transitTime ||
        props.operatedBy) && (
        <div className="space-y-2 sm:space-y-3 border-t pt-3 sm:pt-4">
          {props.flightCheckInTime && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: primaryColor }} />
              <span>
                <span className="hidden sm:inline">Check-in </span>
                <span className="sm:hidden">Check-in: </span>
                {props.flightCheckInTime} minutes before departure
              </span>
            </div>
          )}
          {(props.handLuggageOnly ||
            props.baggageAllowance ||
            props.baggageNotes) && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <Luggage className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: primaryColor }} />
              <div className="flex flex-col">
                {props.handLuggageOnly && <span>Hand luggage only</span>}
                {props.baggageAllowance && (
                  <span>{`${props.baggageAllowance}kg checked baggage included`}</span>
                )}
                {props.baggageNotes && <span>{props.baggageNotes}</span>}
              </div>
            </div>
          )}
          {props.transitTime && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: primaryColor }} />
              <span>
                <span className="hidden sm:inline">Transit time: </span>
                <span className="sm:hidden">Transit: </span>
                {formatTransitTime(props.transitTime)}
              </span>
            </div>
          )}
          {props.operatedBy && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
              <Info className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: primaryColor }} />
              <span>
                <span className="hidden sm:inline">Operated by </span>
                <span className="sm:hidden">Op by: </span>
                {props.operatedBy}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
